// SPA navigation re-assert bus for the persistent background-music bed.
//
// The music provider is a single persistent singleton mounted OUTSIDE
// <BrowserRouter>/<HashRouter>, so it cannot use the router's useLocation() hook.
// And no single DOM event covers every client-side navigation:
//   • history.pushState/replaceState (how react-router pushes) emit NO event;
//   • popstate fires only on back/forward;
//   • hashchange fires only on a manual hash edit — a HashRouter pushState hash
//     write (mobile) does NOT emit it.
// So we patch pushState/replaceState ONCE to emit a synthetic event, and also
// listen for popstate + hashchange. The provider subscribes and calls
// ensurePlaying() on EVERY navigation, so the bed keeps sounding on every screen
// (Library, Store, Practice Playground, games …) without each page opting in —
// the tester "music stops when I navigate" / "no music in the playground" class.
const LOCATION_CHANGE_EVENT = 'lwa:locationchange';
let _patched = false;

function patchHistoryOnce(): void {
  if (_patched) return;
  if (typeof window === 'undefined' || !window.history) return;
  _patched = true;
  const h = window.history as History & { __lwaMusicPatched?: boolean };
  // Guard against a double-wrap (HMR re-eval, a second bundle): wrapping an
  // already-wrapped fn would emit the event twice per navigation.
  if (h.__lwaMusicPatched) return;
  h.__lwaMusicPatched = true;
  const emit = () => {
    try { window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT)); } catch { /* ignore */ }
  };
  const wrap = (name: 'pushState' | 'replaceState') => {
    const orig = h[name];
    if (typeof orig !== 'function') return;
    h[name] = function patched(this: History, ...args: unknown[]): void {
      (orig as (...a: unknown[]) => void).apply(this, args);
      emit();
    } as History[typeof name];
  };
  wrap('pushState');
  wrap('replaceState');
}

/**
 * Subscribe to EVERY SPA navigation (push / replace / back / forward / hash).
 * Returns an unsubscribe that detaches the listeners; the one-time history patch
 * is left in place (idempotent and harmless with zero subscribers).
 */
export function onLocationChange(cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  patchHistoryOnce();
  window.addEventListener(LOCATION_CHANGE_EVENT, cb);
  window.addEventListener('popstate', cb);
  window.addEventListener('hashchange', cb);
  return () => {
    window.removeEventListener(LOCATION_CHANGE_EVENT, cb);
    window.removeEventListener('popstate', cb);
    window.removeEventListener('hashchange', cb);
  };
}
